/**
 * Programs Page - Display available training programs
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import PartialRegistrationPopup from '../components/PartialRegistrationPopup';
import { useAuth } from '../contexts/AuthContext';
import FeatureList from '../components/FeatureList';
import Grid from '../components/Grid';
import ApplyModal from '../components/ApplyModal';
import api from '../api/public';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';

const Programs = () => {
  const { isAuthenticated, isRegistrationComplete, hasPartialRegistration } = useAuth();
  const { theme } = useTheme();
  const [showPartialRegistrationPopup, setShowPartialRegistrationPopup] = useState(false);

  // Apply Modal state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const { showSuccess, showError } = useToast();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch programs from backend on component mount
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch from backend - subdomain is automatically resolved from Host header
        // No need to pass club_slug as query parameter
        const response = await fetch(`/api/public/programs`);

        if (!response.ok) {
          throw new Error(`Failed to fetch programs: ${response.statusText}`);
        }

        const data = await response.json();

        // Map backend response to frontend format
        const formattedPrograms = data.programs.map(program => ({
          id: program.id,
          name: program.name,
          ageGroup: formatAgeGroup(program.min_age, program.max_age),
          description: program.description,
          features: program.skills || [],
          benefits: program.benefits || [],
          minAge: program.min_age || 0 // Keep min_age for sorting
        }));

        // Sort programs: By age group (youngest to oldest), with "Private Coaching" always last
        const sortedPrograms = formattedPrograms.sort((a, b) => {
          const isPrivateCoachingA = a.name.toLowerCase().includes('private') && a.name.toLowerCase().includes('coaching');
          const isPrivateCoachingB = b.name.toLowerCase().includes('private') && b.name.toLowerCase().includes('coaching');

          // If one is Private Coaching and the other isn't, Private Coaching goes last
          if (isPrivateCoachingA && !isPrivateCoachingB) return 1;
          if (!isPrivateCoachingA && isPrivateCoachingB) return -1;

          // Otherwise sort by age group (youngest to oldest)
          return a.minAge - b.minAge;
        });

        setPrograms(sortedPrograms);
      } catch (err) {
        console.error('Error fetching programs:', err);
        setError(err.message);
        // Fall back to empty array instead of showing error to user
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  // Format age group from min/max age values
  const formatAgeGroup = (minAge, maxAge) => {
    if (minAge && maxAge) {
      return `Ages ${minAge}-${maxAge}`;
    } else if (minAge) {
      return `Age ${minAge}+`;
    } else if (maxAge) {
      return `Up to Age ${maxAge}`;
    }
    return 'All Ages';
  };

  const handleEnrollClick = () => {
    if (!isAuthenticated) {
      // Not logged in - redirect to auth
      navigate('/auth');
    } else if (hasPartialRegistration()) {
      // Partial registration - show popup
      setShowPartialRegistrationPopup(true);
    } else if (isRegistrationComplete()) {
      // Complete registration - go to player registration
      navigate('/player-registration');
    }
  };

  const openApplyModal = (programName) => {
    setSelectedProgram(programName);
    setShowApplyModal(true);
  };

  const handleApplySubmit = async (payload) => {
    try {
      await api.submitJoinClubInterest(payload);
      showSuccess('Interest submitted — we will reach out soon');
      setShowApplyModal(false);
    } catch (err) {
      showError(err?.message || 'Failed to submit interest');
      throw err;
    }
  };

  return (
    <PageLayout maxWidth="1400px" className="programs-page">
      <PageHeader
        title="Training Programs"
        subtitle="Age-appropriate soccer training designed to develop skills, character, and love for the game."
      />

      {/* Call to Action */}
      <Section>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <p style={{
          color: 'var(--color-text-secondary)',
          fontSize: '1.1rem',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Explore our training programs below and apply for the ones that fit your needs.
        </p>
      </div>
      </Section>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '3rem 0',
          color: 'var(--color-text-secondary)',
          fontSize: '1.1rem'
        }}>
          Loading programs...
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: 'var(--color-error-light)',
          color: 'var(--color-error)',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <p>Unable to load programs at this time. Please try refreshing the page.</p>
        </div>
      )}

      {/* Programs Grid */}
      {!loading && programs.length > 0 && (
        <Section>
          <Grid minColumnWidth="300px" style={{ marginBottom: '4rem' }}>
            {programs.map(program => (
              <Card
                key={program.id}
                level={2}
                padding={theme.spacing.xl}
                style={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
              >
              {/* Program Name - Heading */}
              <div style={{ marginBottom: '0.75rem' }}>
                <h3 style={{
                  color: 'var(--color-text)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  margin: 0,
                  lineHeight: 1.3
                }}>
                  {program.name}
                </h3>
              </div>

              {/* Age Group - Below title */}
              <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <span style={{
                  background: 'var(--color-primary)',
                  color: 'var(--primary-black)',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  display: 'inline-block'
                }}>
                  {program.ageGroup}
                </span>
              </div>

              {/* Description */}
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '1rem',
                lineHeight: 1.6,
                marginBottom: '1.5rem',
                flexGrow: 1
              }}>
                {program.description}
              </p>

              {/* Skills Section */}
              {program.features && program.features.length > 0 && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '1.5rem',
                  paddingBottom: '1.5rem',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <h4 style={{
                    color: 'var(--color-text)',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    margin: '0 0 1rem 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Skills Focus:</h4>
                  <FeatureList
                    items={program.features}
                  />
                </div>
              )}

              {/* Benefits Section */}
              {program.benefits && program.benefits.length > 0 && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '2rem'
                }}>
                  <h4 style={{
                    color: 'var(--color-text)',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    margin: '0 0 1rem 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Key Benefits:</h4>
                  <FeatureList
                    items={program.benefits}
                    iconType="star"
                  />
                </div>
              )}

              {/* CTA Button */}
              <Button
                variant="primary"
                fullWidth
                style={{ marginTop: 'auto' }}
                onClick={() => openApplyModal(program.name)}
              >
                Learn More
              </Button>
            </Card>
          ))}
        </Grid>
        </Section>
      )}

      {/* Empty State */}
      {!loading && programs.length === 0 && !error && (
        <div style={{
          textAlign: 'center',
          padding: '3rem 0',
          color: 'var(--color-text-secondary)',
          fontSize: '1.1rem'
        }}>
          <p>No programs available at this time.</p>
        </div>
      )}

      {/* Partial Registration Popup */}
      <PartialRegistrationPopup
        isOpen={showPartialRegistrationPopup}
        onClose={() => setShowPartialRegistrationPopup(false)}
      />

      {/* Apply Modal */}
      <ApplyModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        programInterest={selectedProgram}
        onSubmit={handleApplySubmit}
        appliedForRole="parent" // Default to parent/player context for programs
      />
    </PageLayout>
  );
};

export default Programs;


/**
 * 
 * Little Kickers
Ages 6-8
Introduction to soccer fundamentals with fun, age-appropriate activities.

✓Basic ball skills
✓Teamwork games
✓Fun-first approach
✓Parent involvement

Learn More
Rising Stars
Ages 9-11
Building technical skills and soccer understanding for elementary players.

✓Technical skill development
✓Small-sided games
✓Position awareness
✓Character building

Learn More
Future Champions
Ages 12-14
Competitive preparation for middle school players ready for the next level.

✓Advanced techniques
✓Tactical understanding
✓Physical conditioning
✓Mental resilience

Learn More
Private Coaching
Ages 6-14
Personalized one-on-one training sessions tailored to each player's specific needs and goals.

✓Customized training plans
✓Individual skill assessment
✓Accelerated development
✓Flexible scheduling
Key Benefits:
★Faster skill improvement
★Personalized attention
★Goal-specific training
★Immediate feedback

Learn More
 */
